import React from "react";
import { View, Text, StyleSheet, ScrollView, Image } from "react-native";
import { LineChart, Grid, XAxis, YAxis } from "react-native-svg-charts";
import * as shape from "d3-shape";
import { TimeSeriesPoint } from "../models/advistory";
import { normalizeSeries } from "../utils/chart";
import { COMMON_STYLES, TYPOGRAPHY, VARIANTS_STYLES } from "@dehaat/dds";
import images from "../assets/image";
import { Circle } from "react-native-svg";
import { Text as SvgText } from "react-native-svg";

const COLORS = {
  pm: "#6BCF63",
  dm: "#7A6EE6",
};

const Y_TICKS = [0, 15, 30, 45, 60, 75, 90, 100];
const POINT_WIDTH = 80;

interface Props {
  data: TimeSeriesPoint[];
}

export const Legend = () => (
  <View style={styles.legend}>
    <LegendItem color={COLORS.pm} label="Powdery" />
    <LegendItem color={COLORS.dm} label="Downy" />
  </View>
);

const LegendItem = ({ color, label }) => (
  <View style={styles.legendItem}>
    <View style={[styles.dot, { backgroundColor: color }]} />
    <Text style={styles.legendText}>{label}</Text>
  </View>
);

const ranges = ["24h", "48h", "7d", "30d"] as const;

export const RangeTabs = ({ value, onChange }) => (
  <View
    style={[
      COMMON_STYLES.row,
      COMMON_STYLES.alignItemsCenter,
      COMMON_STYLES.mv1,
    ]}
  >
    <Image
      source={images.RiskTrend}
      style={{ width: 16, height: 12, marginRight: 5 }}
    />
    <Text
      style={[
        TYPOGRAPHY.TEXT2,
        VARIANTS_STYLES.MEDIUM,
        { color: "#4B5563", marginRight: 10 },
      ]}
    >
      Risk Trend
    </Text>
    {ranges.map((r) => (
      <Text
        key={r}
        onPress={() => onChange(r)}
        style={[styles.tab, value === r && styles.activeTab]}
      >
        {r}
      </Text>
    ))}
  </View>
);

const Dots = ({ x, y, data, color }) => (
  <>
    {data.map((value, index) => (
      <Circle key={index} cx={x(index)} cy={y(value)} r={4} fill={color} />
    ))}
  </>
);

const ValueLabels = ({ x, y, data, color }) => (
  <>
    {data.map((value, index) => (
      <SvgText
        key={index}
        x={x(index)}
        y={y(value) - 10}
        fontSize={10}
        fill={color}
        alignmentBaseline="middle"
        textAnchor="middle"
      >
        {`${Math.round(value)}%`}
      </SvgText>
    ))}
  </>
);

export const RiskTrendChart: React.FC<Props> = ({ data }) => {
  if (!data || data.length === 0) {
    return <Text style={styles.empty}>No data available</Text>;
  }

  const normalized = normalizeSeries(data);
  const pmData = normalized.map((d) => d.pm);
  const dmData = normalized.map((d) => d.dm);

  const chartWidth = Math.max(normalized.length * POINT_WIDTH, 320);

  return (
    <View style={styles.root}>
      <YAxis
        data={Y_TICKS}
        min={0}
        max={100}
        numberOfTicks={Y_TICKS.length}
        svg={styles.axis}
        contentInset={styles.inset}
        formatLabel={(v) => `${v}%`}
      />

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ width: chartWidth }}
      >
        <View>
          <View style={[styles.chartArea, { width: chartWidth }]}>
            <Grid
              direction={Grid.Direction.HORIZONTAL}
              svg={{ stroke: "#DADADA", strokeWidth: 1 }}
            />

            <LineChart
              style={StyleSheet.absoluteFill}
              data={pmData}
              svg={{ stroke: COLORS.pm, strokeWidth: 3 }}
              contentInset={styles.inset}
              curve={shape.curveNatural}
            >
              <Dots
                data={pmData}
                color={COLORS.pm}
                x={undefined}
                y={undefined}
              />
              <ValueLabels
                data={pmData}
                color={COLORS.pm}
                x={undefined}
                y={undefined}
              />
            </LineChart>

            <LineChart
              style={StyleSheet.absoluteFill}
              data={dmData}
              svg={{ stroke: COLORS.dm, strokeWidth: 3 }}
              contentInset={styles.inset}
              curve={shape.curveNatural}
            >
              <Dots
                data={dmData}
                color={COLORS.dm}
                x={undefined}
                y={undefined}
              />
              <ValueLabels
                data={dmData}
                color={COLORS.dm}
                x={undefined}
                y={undefined}
              />
            </LineChart>
          </View>

          <XAxis
            style={styles.xAxis}
            data={normalized}
            svg={{
              ...styles.axis,
              textAnchor: "middle",
            }}
            contentInset={{ left: 10, right: 10 }}
            formatLabel={(_, index) => normalized[index]?.time}
          />
        </View>
      </ScrollView>
    </View>
  );
};

/* -------------------- STYLES -------------------- */

const styles = StyleSheet.create({
  root: {
    flexDirection: "row",
    height: 280,
  },

  chartArea: {
    height: 250,
  },

  inset: {
    top: 40,
    bottom: 40,
  },

  axis: {
    fontSize: 12,
    fill: "#9AA0A6",
  },

  xAxis: {
    marginTop: 10,
    height: 40,
  },

  empty: {
    padding: 16,
    color: "#999",
  },

  /* -------- Legend -------- */

  legend: {
    flexDirection: "row",
    marginLeft: 16,
    marginTop: 8,
  },

  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 16,
  },

  legendText: {
    fontSize: 14,
  },

  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 6,
  },

  /* -------- Tabs -------- */

  tab: {
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 20,
    backgroundColor: "#F1F3F5",
    marginRight: 8,
    fontSize: 14,
  },

  activeTab: {
    backgroundColor: "#212529",
    color: "#FFFFFF",
  },
});
